import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Box,
  Autocomplete,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Add, Close} from '@mui/icons-material';
import moment from 'moment';
import { getIn } from 'formik';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchJobCategories } from '@/store';
import { useAuth } from '@/hooks';
import { SlateRichTextEditor } from './SlateRichTextEditor';

interface JobPostFormProps {
  formik: {
    values: any;
    errors: any;
    touched: any;
    handleChange: (e: any) => void;
    handleBlur: (e: any) => void;
    setFieldValue: (field: string, value: any) => void;
    setFieldTouched: (field: string, value: any) => void;
  };
  feedType?: string;
}

export const JobPostForm: React.FC<JobPostFormProps> = ({ formik, feedType }) => {
  const dispatch = useAppDispatch();
  const { jobCategories } = useAppSelector((state) => state.jobCategory);
  // const { companies } = useAppSelector((state) => state.company);
  const { user } = useAuth();
  
  const [skillInput, setSkillInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  // Memoize callbacks to prevent unnecessary re-renders
  // Formik's setFieldValue and setFieldTouched are stable functions
  const handleJobDescriptionChange = useCallback((value: string) => {
    formik.setFieldValue('feedSpecificData.jobDescription', value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJobDescriptionBlur = useCallback(() => {
    formik.setFieldTouched('feedSpecificData.jobDescription', true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(fetchJobCategories({ isActive: true , limit:500}));
    // dispatch(fetchCompanies({ isActive: true,limit:500 }));
  }, [dispatch]);
  
  // const hasImage = formik.values.media?.some((item: any) => item.type === 'image');
  // const hasDocument = formik.values.media?.some((item: any) => item.type === 'document');
  
  const getFormTitle = () => {
    if (feedType?.toLowerCase() === 'job') {
      return 'Job Posting Details';
    }
    return 'Post Details';
  };
  
  const getFormDescription = () => {
    if (feedType?.toLowerCase() === 'job') {
      return 'Fill in the job details to attract the right candidates';
    }
    return 'Share your content with the community';
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !formik.values.feedSpecificData?.skills?.includes(skillInput.trim())) {
      const currentSkills = formik.values.feedSpecificData?.skills || [];
      formik.setFieldValue('feedSpecificData.skills', [...currentSkills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    const currentSkills = formik.values.feedSpecificData?.skills || [];
    formik.setFieldValue('feedSpecificData.skills', currentSkills.filter((skill: string) => skill !== skillToRemove));
  };

  const handleAddLocation = () => {
    if (locationInput.trim() && !formik.values.feedSpecificData?.location?.includes(locationInput.trim())) {
      const currentLocations = formik.values.feedSpecificData?.location || [];
      formik.setFieldValue('feedSpecificData.location', [...currentLocations, locationInput.trim()]);
      // Also update the main feed location with the first location
      if (currentLocations.length === 0) {
        formik.setFieldValue('location', locationInput.trim());
      }
      setLocationInput('');
    }
  };

  const handleRemoveLocation = (locationToRemove: string) => {
    const currentLocations = formik.values.feedSpecificData?.location || [];
    const updatedLocations = currentLocations.filter((loc: string) => loc !== locationToRemove);
    formik.setFieldValue('feedSpecificData.location', updatedLocations);
    // Update main feed location
    if (updatedLocations.length > 0) {
      formik.setFieldValue('location', updatedLocations[0]);
    } else {
      formik.setFieldValue('location', '');
    }
  };
  
  const handleAddTag = () => {
    if (tagInput.trim() && !formik.values.tags?.includes(tagInput.trim())) {
      const currentTags = formik.values.tags || [];
      formik.setFieldValue('tags', [...currentTags, tagInput.trim()]);
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = formik.values.tags || [];
    formik.setFieldValue('tags', currentTags.filter((tag: string) => tag !== tagToRemove));
  };
  
  // const handleMediaUpload = (type: 'image' | 'document') => {
  //   // This would trigger file upload - implementation depends on your upload system
  //   console.log('Upload', type);
  // };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
    <Box>
      {/* Dynamic Form Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {getFormTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getFormDescription()}
        </Typography>
      </Box>
      
    <Grid container spacing={3}>
      {/* Job Title */}
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          name="feedSpecificData.jobTitle"
          label="Looking for"
          required
          placeholder="e.g. Senior Software Engineer"
          value={formik.values.feedSpecificData?.jobTitle || ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.feedSpecificData?.jobTitle && Boolean(formik.errors.feedSpecificData?.jobTitle)}
          helperText={formik.touched.feedSpecificData?.jobTitle && formik.errors.feedSpecificData?.jobTitle}
          slotProps={{ 
            input: {
              // startAdornment: <InputAdornment position="start"><Work /></InputAdornment>,
            },
            htmlInput: { maxLength: 100 } 
          }}
        />
      </Grid>

      {/* Job Description */}
      <Grid size={{ xs: 12 }}>
        <SlateRichTextEditor
          value={formik.values.feedSpecificData?.jobDescription || ''}
          onChange={handleJobDescriptionChange}
          onBlur={handleJobDescriptionBlur}
          error={formik.touched.feedSpecificData?.jobDescription && Boolean(formik.errors.feedSpecificData?.jobDescription)}
          helperText={formik.touched.feedSpecificData?.jobDescription && formik.errors.feedSpecificData?.jobDescription}
          placeholder="Describe the role, responsibilities, and requirements..."
          maxLength={5000}
          title='Job Requirement'
        />
      </Grid>

      {/* Job Category and Company */}
      <Grid size={{ xs: 12, md: 12 }}>
        <Autocomplete
          options={jobCategories}
          getOptionLabel={(option) => option.name}
          value={jobCategories.find(cat => cat._id === formik.values.feedSpecificData?.jobCategoryId) || null}
          onChange={(_, newValue) => {
            formik.setFieldValue('feedSpecificData.jobCategoryId', newValue?._id || '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              name="feedSpecificData.jobCategoryId"
              label="Job Category"
              required
              error={formik.touched.feedSpecificData?.jobCategoryId && Boolean(formik.errors.feedSpecificData?.jobCategoryId)}
              helperText={formik.touched.feedSpecificData?.jobCategoryId && formik.errors.feedSpecificData?.jobCategoryId}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {/* <InputAdornment position="start">
                        <Category />
                      </InputAdornment> */}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>

      

      {/* Company Address Information */}
    

      {/* Job Type and Experience Level */}
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          select
          name="feedSpecificData.jobType"
          label="Job Type"
          required
          value={formik.values.feedSpecificData?.jobType || ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.feedSpecificData?.jobType && Boolean(formik.errors.feedSpecificData?.jobType)}
          helperText={formik.touched.feedSpecificData?.jobType && formik.errors.feedSpecificData?.jobType}
        >
          <MenuItem value="full-time">Full-time</MenuItem>
          <MenuItem value="part-time">Part-time</MenuItem>
          <MenuItem value="contract">Contract</MenuItem>
          <MenuItem value="freelance">Freelance</MenuItem>
          <MenuItem value="internship">Internship</MenuItem>
        </TextField>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          select
          name="feedSpecificData.experienceLevel"
          label="Experience Level"
          required
          value={formik.values.feedSpecificData?.experienceLevel || ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.feedSpecificData?.experienceLevel && Boolean(formik.errors.feedSpecificData?.experienceLevel)}
          helperText={formik.touched.feedSpecificData?.experienceLevel && formik.errors.feedSpecificData?.experienceLevel}
        >
          <MenuItem value="entry">Fresher / Entry Level – 0 to 1 Year</MenuItem>
          <MenuItem value="junior">Junior Level – 1 to 3 Years</MenuItem>
          <MenuItem value="mid">Mid-Level – 3 to 5 Years</MenuItem>
          <MenuItem value="senior">Senior Level – 5 to 8 Years</MenuItem>
          <MenuItem value="manager">Managerial Level – 8 to 12 Years</MenuItem>
          <MenuItem value="head">Leadership / Head Level – 12 to 18 Years</MenuItem>
          <MenuItem value="executive">Director / C-Level – 18+ Years</MenuItem>
        </TextField>
      </Grid>

      {/* Number of Vacancies and Notice Type */}
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          name="feedSpecificData.noOfVacancy"
          label="Number of Vacancies"
          type="number"
          required
          value={formik.values.feedSpecificData?.noOfVacancy || 1}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.feedSpecificData?.noOfVacancy && Boolean(formik.errors.feedSpecificData?.noOfVacancy)}
          helperText={formik.touched.feedSpecificData?.noOfVacancy && formik.errors.feedSpecificData?.noOfVacancy}
          slotProps={{ 
            input: {
              // startAdornment: <InputAdornment position="start"><Person /></InputAdornment>,
            },
            htmlInput: { min: 1, max: 100 } 
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          select
          name="feedSpecificData.noticeType"
          label="Notice Period"
          required
          value={formik.values.feedSpecificData?.noticeType || ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.feedSpecificData?.noticeType && Boolean(formik.errors.feedSpecificData?.noticeType)}
          helperText={formik.touched.feedSpecificData?.noticeType && formik.errors.feedSpecificData?.noticeType}
        >
          <MenuItem value="immediate">Immediate</MenuItem>
          <MenuItem value="15-days">15 Days</MenuItem>
          <MenuItem value="30-days">30 Days</MenuItem>
          <MenuItem value="60-days">60 Days</MenuItem>
          <MenuItem value="90-days">90 Days</MenuItem>
        </TextField>
      </Grid>
  <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Company Address
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12}}>
        <TextField
          fullWidth
          name="feedSpecificData.companyInfo.companyname"
          label="Company Name"
          required
          disabled
          value={user?.currentCompanyInfo?.companyname || ''}
          error={!user?.currentCompanyInfo?.companyname}
          helperText={!user?.currentCompanyInfo?.companyname ? 'Please update your company information in profile' : ''}
          slotProps={{ 
            input: {
              // startAdornment: <InputAdornment position="start"><Domain /></InputAdornment>,
            },
          }}
        />
      </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              name="feedSpecificData.companyInfo.addressLine1"
              label="Address Line 1"
              required
              disabled
              value={user?.currentCompanyInfo?.addressLine1 || ''}
              error={!user?.currentCompanyInfo?.addressLine1}
              helperText={!user?.currentCompanyInfo?.addressLine1 ? 'Please update your company information in profile' : ''}
              size="small"
              slotProps={{ 
                input: {
                  // startAdornment: <InputAdornment position="start"><Business /></InputAdornment>,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              name="feedSpecificData.companyInfo.addressLine2"
              label="Address Line 2"
              disabled
              value={user?.currentCompanyInfo?.addressLine2 || ''}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              name="feedSpecificData.companyInfo.city"
              label="City"
              required
              disabled
              value={user?.currentCompanyInfo?.city || ''}
              error={!user?.currentCompanyInfo?.city}
              helperText={!user?.currentCompanyInfo?.city ? 'Please update your company information in profile' : ''}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              name="feedSpecificData.companyInfo.state"
              label="State"
              required
              disabled
              value={user?.currentCompanyInfo?.state || ''}
              error={!user?.currentCompanyInfo?.state}
              helperText={!user?.currentCompanyInfo?.state ? 'Please update your company information in profile' : ''}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              name="feedSpecificData.companyInfo.pincode"
              label="Pincode"
              required
              disabled
              value={user?.currentCompanyInfo?.pincode || ''}
              error={!user?.currentCompanyInfo?.pincode}
              helperText={!user?.currentCompanyInfo?.pincode ? 'Please update your company information in profile' : ''}
              size="small"
            />
          </Grid>
        </Grid>
      </Grid>
      {/* Optional Fields Section */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" sx={{ mt: 2, mb: 0 }}>
          Additional Details
        </Typography>
      </Grid>

      {/* Work Mode */}
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          select
          name="feedSpecificData.workMode"
          label="Work Mode"
              required

          value={formik.values.feedSpecificData?.workMode || ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.feedSpecificData?.workMode && Boolean(formik.errors.feedSpecificData?.workMode)}
          helperText={formik.touched.feedSpecificData?.workMode && formik.errors.feedSpecificData?.workMode}
        >
          <MenuItem value="">Select work mode</MenuItem>
          <MenuItem value="remote">Remote</MenuItem>
          <MenuItem value="onsite">On-site</MenuItem>
          <MenuItem value="hybrid">Hybrid</MenuItem>
        </TextField>
      </Grid>

      {/* Application Deadline */}
      <Grid size={{ xs: 12, md: 6 }}>
        <DatePicker
          label="Application Deadline"
          value={formik.values.feedSpecificData?.applicationDeadline ? moment(formik.values.feedSpecificData.applicationDeadline) : null}
          onChange={(newValue) => {
            formik.setFieldValue('feedSpecificData.applicationDeadline', newValue ? newValue.format('YYYY-MM-DD') : '');
          }}
          minDate={moment().add(1, 'day')}
          slotProps={{
            textField: {
              fullWidth: true,
              required:true,

              size: 'small',
              error: formik.touched.feedSpecificData?.applicationDeadline && Boolean(formik.errors.feedSpecificData?.applicationDeadline),
              helperText: formik.touched.feedSpecificData?.applicationDeadline && formik.errors.feedSpecificData?.applicationDeadline,
            }
          }}
        />
      </Grid>

      {/* Salary Range */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Salary Range
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              name="feedSpecificData.salary.min"
              label="Min Salary"
              type="number"
              required
              value={formik.values.feedSpecificData?.salary?.min || ''}
              onChange={(e) => {
                formik.handleChange(e);
                // Also set touched for salary object
                formik.setFieldTouched('feedSpecificData.salary', true);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.feedSpecificData?.salary?.min && Boolean(formik.errors.feedSpecificData?.salary?.min)}
              helperText={formik.touched.feedSpecificData?.salary?.min && formik.errors.feedSpecificData?.salary?.min}
              size="small"
              slotProps={{ htmlInput: { min: 0 } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              name="feedSpecificData.salary.max"
              label="Max Salary"
              type="number"
              required
              value={formik.values.feedSpecificData?.salary?.max || ''}
              onChange={(e) => {
                formik.handleChange(e);
                // Also set touched for salary object
                formik.setFieldTouched('feedSpecificData.salary', true);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.feedSpecificData?.salary?.max && Boolean(formik.errors.feedSpecificData?.salary?.max)}
              helperText={formik.touched.feedSpecificData?.salary?.max && formik.errors.feedSpecificData?.salary?.max}
              size="small"
              slotProps={{ htmlInput: { min: 0 } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              select
              name="feedSpecificData.salary.denomination"
              label="Denomination"
              required
              value={formik.values.feedSpecificData?.salary?.denomination || 'lakh'}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.feedSpecificData?.salary?.denomination && Boolean(formik.errors.feedSpecificData?.salary?.denomination)}
              helperText={formik.touched.feedSpecificData?.salary?.denomination && formik.errors.feedSpecificData?.salary?.denomination}
              size="small"
            >
              <MenuItem value="thousand">Thousand</MenuItem>
              <MenuItem value="lakh">Lakh</MenuItem>
              <MenuItem value="crore">Crore</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              select
              name="feedSpecificData.salary.period"
              label="Period"
              required
              value={formik.values.feedSpecificData?.salary?.period || 'yearly'}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.feedSpecificData?.salary?.period && Boolean(formik.errors.feedSpecificData?.salary?.period)}
              helperText={formik.touched.feedSpecificData?.salary?.period && formik.errors.feedSpecificData?.salary?.period}
              size="small"
            >
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="hourly">Hourly</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        {/* Salary validation error */}
        {formik.touched.feedSpecificData?.salary && formik.errors.feedSpecificData?.salary && typeof formik.errors.feedSpecificData.salary === 'string' && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {formik.errors.feedSpecificData.salary}
          </Typography>
        )}
      </Grid>

      {/* Skills */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Required Skills
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Add skill"
            value={skillInput}
            required
            error={formik.touched.feedSpecificData?.skills && Boolean(formik.errors.feedSpecificData?.skills)}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddSkill();
              }
            }}
            sx={{ flex: 1 }}
            placeholder="e.g. HR Executive, Finance"
          />
          <IconButton
            onClick={handleAddSkill}
            disabled={!skillInput.trim()}
            size="small"
            color="primary"
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              height: 28,
              width: 28,
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'action.disabled', color: 'action.disabled' }
            }}
          >
            <Add />
          </IconButton>
        </Box>
        {formik.values.feedSpecificData?.skills && formik.values.feedSpecificData.skills.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formik.values.feedSpecificData.skills.map((skill: string, index: number) => (
              <Chip
                key={index}
                label={skill}
                onDelete={() => handleRemoveSkill(skill)}
                size="small"
                color="primary"
                variant="outlined"
                deleteIcon={<Close fontSize="small" />}
              />
            ))}
          </Box>
        )}
        {formik.touched.feedSpecificData?.skills && formik.errors.feedSpecificData?.skills && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {formik.errors.feedSpecificData.skills}
          </Typography>
        )}
      </Grid>

      {/* Job Locations */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Job Locations 
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Add location"
            required
            error={formik.touched.feedSpecificData?.location && Boolean(formik.errors.feedSpecificData?.location)}
            value={locationInput}
            onChange={(e) => setLocationInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddLocation();
              }
            }}
            sx={{ flex: 1 }}
            placeholder="e.g. Chennai, Bangalore, Remote"
          />
          <IconButton
            onClick={handleAddLocation}
            disabled={!locationInput.trim()}
            size="small"
            color="info"
            sx={{ 
              bgcolor: 'info.main', 
              color: 'white',
                 height: 28,
              width: 28,
              '&:hover': { bgcolor: 'info.dark' },
              '&:disabled': { bgcolor: 'action.disabled', color: 'action.disabled' }
            }}
          >
            <Add />
          </IconButton>
        </Box>
        {formik.values.feedSpecificData?.location && formik.values.feedSpecificData.location.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formik.values.feedSpecificData.location.map((location: string, index: number) => (
              <Chip
                key={index}
                label={location}
                onDelete={() => handleRemoveLocation(location)}
                size="small"
                color="info"
                variant="outlined"
                deleteIcon={<Close fontSize="small" />}
              />
            ))}
          </Box>
        )}
        {formik.touched.feedSpecificData?.location && formik.errors.feedSpecificData?.location && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {formik.errors.feedSpecificData.location}
          </Typography>
        )}
      </Grid>

      {/* Contact Details */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Contact Details (Optional)
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              name="feedSpecificData.contactDetails.email"
              label="Contact Email"
              type="email"
              placeholder="example@company.com"
              value={formik.values.feedSpecificData?.contactDetails?.email || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                getIn(formik.touched, 'feedSpecificData.contactDetails.email') &&
                Boolean(getIn(formik.errors, 'feedSpecificData.contactDetails.email'))
              }
              helperText={
                getIn(formik.touched, 'feedSpecificData.contactDetails.email') &&
                getIn(formik.errors, 'feedSpecificData.contactDetails.email')
              }
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              name="feedSpecificData.contactDetails.mobile"
              label="Contact Mobile"
              placeholder="10 digit mobile number"
              value={formik.values.feedSpecificData?.contactDetails?.mobile || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                getIn(formik.touched, 'feedSpecificData.contactDetails.mobile') &&
                Boolean(getIn(formik.errors, 'feedSpecificData.contactDetails.mobile'))
              }
              helperText={
                getIn(formik.touched, 'feedSpecificData.contactDetails.mobile') &&
                getIn(formik.errors, 'feedSpecificData.contactDetails.mobile')
              }
              size="small"
              slotProps={{
                htmlInput: { maxLength: 10 }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      
      {/* Tags */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          HashTag
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Add tag"
            required
            error={formik.touched.tags && Boolean(formik.errors.tags)}
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
            sx={{ flex: 1 }}
            placeholder="e.g. remote, startup, tech"
          />
          <IconButton
            onClick={handleAddTag}
            disabled={!tagInput.trim()}
            size="small"
            color="secondary"
            sx={{ 
              bgcolor: 'secondary.main', 
              color: 'white',
               height: 28,
              width: 28,
              '&:hover': { bgcolor: 'secondary.dark' },
              '&:disabled': { bgcolor: 'action.disabled', color: 'action.disabled' }
            }}
          >
            <Add />
          </IconButton>
        </Box>
        {formik.values.tags && formik.values.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formik.values.tags.map((tag: string, index: number) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                size="small"
                color="secondary"
                variant="outlined"
                deleteIcon={<Close fontSize="small" />}
              />
            ))}
          </Box>
        )}
        {formik.touched.tags && formik.errors.tags && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {formik.errors.tags}
          </Typography>
        )}
      </Grid>
 
    </Grid>
    </Box>
    </LocalizationProvider>
  );
};